/**
 * Category Store — manajemen state kategori aktivitas EKINKINIAN.
 *
 * Menggunakan Svelte 5 runes ($state) di module scope (.svelte.ts)
 * agar state reaktif global. Komponen mengakses via getter `category`.
 *
 * Tanggung jawab:
 * - Memuat daftar kategori untuk workspace aktif dari SQLite cache.
 * - Auto-seed kategori default saat workspace baru (D1).
 * - Operasi CRUD kategori (create, update, delete).
 *
 * Acuan: docs/01-SRS.md FR-ACT-01, docs/09-Roadmap.md S8
 */
import { CategoryRepository } from './db';
import type { CategoryCache, UpdateCategoryInput } from './db';

// ---------------------------------------------------------------------------
// Repository singleton
// ---------------------------------------------------------------------------
const categoryRepo = new CategoryRepository();

// ---------------------------------------------------------------------------
// Constants — D1: Default Categories
// ---------------------------------------------------------------------------

/** Kategori default yang di-seed saat workspace baru dibuat (D1). */
export const DEFAULT_CATEGORIES = [
  { name: 'Umum', color: '#8B6F47', icon: '📁' },
  { name: 'Rapat', color: '#0EA5E9', icon: '🤝' },
  { name: 'Dinas Luar', color: '#16A34A', icon: '🚗' },
  { name: 'Dokumen', color: '#C9A227', icon: '📄' },
] as const;

// ---------------------------------------------------------------------------
// Reactive state (module-level, Svelte 5 runes)
// ---------------------------------------------------------------------------
let _categories = $state<CategoryCache[]>([]);
let _loading = $state(false);
let _error = $state<string | null>(null);

/** Workspace ID yang saat ini memiliki state ini. Null = belum di-init. */
let _initializedWorkspaceId: string | null = null;

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

/**
 * Inisialisasi category store untuk workspace tertentu.
 *
 * Alur:
 * 1. Skip jika sudah di-init untuk workspace yang sama (idempotent).
 * 2. Muat semua kategori dari SQLite cache.
 * 3. Jika kosong → seed kategori default (D1).
 *
 * @param workspaceId ID workspace aktif.
 * @param tenantId ID tenant (pseudo = userId sampai S14).
 */
export async function initCategories(workspaceId: string, tenantId: string): Promise<void> {
  if (_initializedWorkspaceId === workspaceId) return;
  _initializedWorkspaceId = workspaceId;

  _loading = true;
  _error = null;

  try {
    // D1: Seed default categories jika workspace belum punya kategori
    await categoryRepo.seedDefaults(workspaceId, tenantId, [...DEFAULT_CATEGORIES]);
    _categories = await categoryRepo.findByWorkspace(workspaceId);
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal memuat kategori';
    console.error('[category] Init failed:', err);
  } finally {
    _loading = false;
  }
}

// ---------------------------------------------------------------------------
// CRUD Operations
// ---------------------------------------------------------------------------

/**
 * Buat kategori baru.
 * @returns Kategori yang dibuat, atau null jika gagal.
 */
export async function createCategory(
  name: string,
  color: string = '#8B6F47',
  icon: string | null = null,
): Promise<CategoryCache | null> {
  const workspaceId = _initializedWorkspaceId;
  if (!workspaceId) {
    _error = 'Belum ada workspace aktif — tidak dapat membuat kategori';
    return null;
  }

  const trimmedName = name.trim();
  if (!trimmedName) {
    _error = 'Nama kategori tidak boleh kosong';
    return null;
  }

  try {
    const created = await categoryRepo.create({
      workspace_id: workspaceId,
      tenant_id: workspaceId, // pseudo-tenant sampai S14
      name: trimmedName,
      color,
      icon,
      sort_order: _categories.length,
    });
    _categories = [..._categories, created];
    return created;
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal membuat kategori';
    console.error('[category] Create failed:', err);
    return null;
  }
}

/**
 * Perbarui kategori yang ada.
 * @returns Kategori yang diperbarui, atau null jika gagal.
 */
export async function updateCategory(
  id: string,
  changes: UpdateCategoryInput,
): Promise<CategoryCache | null> {
  try {
    const updated = await categoryRepo.update(id, changes);
    if (updated) {
      _categories = _categories.map((c) => (c.id === id ? updated : c));
    }
    return updated;
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal memperbarui kategori';
    console.error('[category] Update failed:', err);
    return null;
  }
}

/**
 * Hapus kategori (soft delete via BaseRepository).
 * Catatan: aktivitas yang menggunakan kategori ini akan tetap ada,
 * hanya category_id-nya yang menjadi null.
 */
export async function deleteCategory(id: string): Promise<void> {
  try {
    await categoryRepo.softDelete(id);
    _categories = _categories.filter((c) => c.id !== id);
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal menghapus kategori';
    console.error('[category] Delete failed:', err);
  }
}

// ---------------------------------------------------------------------------
// Refresh & Reset
// ---------------------------------------------------------------------------

/** Muat ulang daftar kategori dari cache. */
export async function refreshCategories(): Promise<void> {
  const workspaceId = _initializedWorkspaceId;
  if (!workspaceId) return;

  try {
    _categories = await categoryRepo.findByWorkspace(workspaceId);
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal memuat ulang kategori';
    console.error('[category] Refresh failed:', err);
  }
}

/** Reset state kategori (dipanggil saat logout atau workspace switch). */
export function resetCategories(): void {
  _categories = [];
  _loading = false;
  _error = null;
  _initializedWorkspaceId = null;
}

/** Hapus pesan error saat ini. */
export function clearCategoryError(): void {
  _error = null;
}

// ---------------------------------------------------------------------------
// Reactive Accessors
// ---------------------------------------------------------------------------

/** Akses reaktif ke state kategori. */
export const category = {
  /** Daftar semua kategori di workspace aktif. */
  get categories(): CategoryCache[] {
    return _categories;
  },
  /** Cari kategori berdasarkan ID. */
  getById(id: string): CategoryCache | null {
    return _categories.find((c) => c.id === id) ?? null;
  },
  /** Nama kategori berdasarkan ID (untuk tampilan cepat). */
  getName(id: string | null): string | null {
    if (!id) return null;
    return _categories.find((c) => c.id === id)?.name ?? null;
  },
  /** True jika sedang memuat. */
  get loading(): boolean {
    return _loading;
  },
  /** Pesan error terakhir, atau null. */
  get error(): string | null {
    return _error;
  },
  /** Jumlah kategori. */
  get count(): number {
    return _categories.length;
  },
  /** True jika store sudah di-init untuk suatu workspace. */
  get isInitialized(): boolean {
    return _initializedWorkspaceId !== null;
  },
};
